import { Injectable, signal, computed } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError, shareReplay, finalize, of, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AuthResponse,
  AuthTokens,
  ChangePasswordPayload,
  ForgotPasswordPayload,
  LoginPayload,
  OnboardingPayload,
  RegisterPayload,
  ResetPasswordPayload,
  UserProfile,
} from '../models';

const TOKEN_KEY = 'camvendors_access';
const REFRESH_KEY = 'camvendors_refresh';
const USER_KEY = 'camvendors_user';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly apiUrl = `${environment.authApiUrl}/auth`;
  private syncSessionObservable$: Observable<UserProfile> | null = null;
  private refreshObservable$: Observable<AuthResponse> | null = null;

  /** Reactive user state. */
  private readonly _user = signal<UserProfile | null>(this.loadStoredUser());
  readonly user = this._user.asReadonly();
  readonly loggingOut = signal(false);
  readonly isAuthenticated = computed(() => !!this._user());
  readonly isVendor = computed(() => this._user()?.userType === 'vendor');
  readonly isCam = computed(() => this._user()?.userType === 'cam');
  readonly isAdmin = computed(() => this._user()?.roles?.includes('admin') ?? false);
  readonly isOnboarded = computed(() => this._user()?.isOnboarded ?? false);

  private _isSynced = false;

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router
  ) {}

  /** Ensures that session is synced from cookies if not already done. */
  ensureSessionSynced(): Observable<boolean> {
    if (this._isSynced) {
      return of(true);
    }

    return this.syncSessionFromCookies().pipe(
      map(() => {
        this._isSynced = true;
        return true;
      }),
      catchError(() => {
        this._isSynced = true;
        return of(true);
      })
    );
  }

  /** Register a new local account. */
  register(payload: RegisterPayload): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/register`, payload, { withCredentials: true }).pipe(
      tap((res) => this.handleAuthSuccess(res)),
      catchError(this.handleError)
    );
  }

  /** Login with email/password. */
  login(payload: LoginPayload): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/login`, payload, { withCredentials: true }).pipe(
      tap((res) => this.handleAuthSuccess(res)),
      catchError(this.handleError)
    );
  }

  /** Refresh the access token. */
  refresh(): Observable<AuthResponse> {
    if (this.refreshObservable$) {
      return this.refreshObservable$;
    }

    const refreshToken = this.getRefreshToken();
    const body = refreshToken ? { refreshToken } : {};

    this.refreshObservable$ = this.http
      .post<AuthResponse>(`${this.apiUrl}/refresh`, body, { withCredentials: true })
      .pipe(
        tap((res) => this.handleAuthSuccess(res)),
        catchError((err) => this.handleError(err)),
        shareReplay(1),
        finalize(() => {
          this.refreshObservable$ = null;
        })
      );

    return this.refreshObservable$;
  }

  /**
   * After OAuth callback, the backend has set HttpOnly cookies.
   * This method calls GET /auth/me (cookies sent automatically) to fetch
   * the user profile and sync frontend state.
   *
   * Note: We deduplicate simultaneous requests using syncSessionObservable$ to prevent
   * race conditions on browser cookie initialization (e.g. during Microsoft OAuth redirects).
   */
  syncSessionFromCookies(): Observable<UserProfile> {
    if (this.syncSessionObservable$) {
      return this.syncSessionObservable$;
    }

    this.syncSessionObservable$ = this.http.get<UserProfile>(`${this.apiUrl}/me`, { withCredentials: true }).pipe(
      tap((user) => {
        this._user.set(user);
        localStorage.setItem(USER_KEY, JSON.stringify(user));
      }),
      shareReplay(1),
      finalize(() => {
        this.syncSessionObservable$ = null;
      })
    );

    return this.syncSessionObservable$;
  }

  /** Complete onboarding. */
  onboarding(payload: OnboardingPayload): Observable<any> {
    return this.http.post(`${this.apiUrl}/onboarding`, payload, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  /** Get current user profile. */
  me(): Observable<UserProfile> {
    return this.http.get<UserProfile>(`${this.apiUrl}/me`, { withCredentials: true }).pipe(
      tap((user) => {
        this._user.set(user);
        localStorage.setItem(USER_KEY, JSON.stringify(user));
      }),
      catchError(this.handleError)
    );
  }

  /** Forgot password — request reset email. */
  forgotPassword(payload: ForgotPasswordPayload): Observable<any> {
    return this.http.post(`${this.apiUrl}/password/forgot`, payload).pipe(
      catchError(this.handleError)
    );
  }

  /** Reset password with token. */
  resetPassword(payload: ResetPasswordPayload): Observable<any> {
    return this.http.post(`${this.apiUrl}/password/reset`, payload).pipe(
      catchError(this.handleError)
    );
  }

  /** Change password (authenticated). */
  changePassword(payload: ChangePasswordPayload): Observable<any> {
    return this.http.post(`${this.apiUrl}/password/change`, payload, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  /** Verify email address with action token. */
  verifyEmail(token: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/email/verify`, { token }).pipe(
      catchError(this.handleError)
    );
  }

  /** Resend verification email. */
  resendVerification(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/email/resend-verification`, { email }).pipe(
      catchError(this.handleError)
    );
  }

  /** Logout and clear state. */
  logout(): void {
    this.loggingOut.set(true);
    const refreshToken = this.getRefreshToken();
    this.http
      .post(`${this.apiUrl}/logout`, { refreshToken: refreshToken || null }, { withCredentials: true })
      .pipe(
        finalize(() => {
          this.clearSession();
          this.router.navigate(['/auth/login'], { replaceUrl: true }).then(() => {
            this.loggingOut.set(false);
          });
        })
      )
      .subscribe();
  }

  startOAuth(provider: 'google' | 'microsoft', userType: 'cam' | 'vendor' = 'vendor', isOrganization = false, organizationName = ''): void {
    let url = `${environment.oauthBaseUrl}/auth/oauth/${provider}/start?userType=${userType}`;
    if (userType === 'cam' && isOrganization) {
      url += `&isOrganization=true&organizationName=${encodeURIComponent(organizationName)}`;
    }
    window.location.href = url;
  }

  /** Called by the OAuth callback to sync user profile into state. */
  setUserFromOAuth(user: UserProfile): void {
    this._user.set(user);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  /** Get current user's organization details. */
  getOrganization(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/organization`, { withCredentials: true }).pipe(catchError(this.handleError));
  }

  /** Send organization invitation. */
  sendInvitation(email: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/invitations/send`, { email }, { withCredentials: true }).pipe(catchError(this.handleError));
  }

  /** Verify organization invitation token. */
  verifyInvitation(token: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/invitations/verify?token=${token}`, { withCredentials: true }).pipe(catchError(this.handleError));
  }

  /** Accept organization invitation. */
  acceptInvitation(token: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/invitations/accept`, { token }, { withCredentials: true }).pipe(catchError(this.handleError));
  }

  /** Delete/revoke organization invitation. */
  deleteInvitation(invitationId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/invitations/${invitationId}`, { withCredentials: true }).pipe(catchError(this.handleError));
  }

  /** Remove/expel a member from the organization. */
  expelMember(memberId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/organization/members/${memberId}`, { withCredentials: true }).pipe(catchError(this.handleError));
  }

  // ── Admin: User Management ──────────────────────────────────

  /** List users (admin). */
  listUsers(params?: { userType?: string; isActive?: string; search?: string; limit?: number; offset?: number }): Observable<any> {
    let httpParams = new HttpParams();
    if (params?.userType) httpParams = httpParams.set('userType', params.userType);
    if (params?.isActive) httpParams = httpParams.set('isActive', params.isActive);
    if (params?.search) httpParams = httpParams.set('search', params.search);
    if (params?.limit) httpParams = httpParams.set('limit', params.limit.toString());
    if (params?.offset) httpParams = httpParams.set('offset', params.offset.toString());
    return this.http.get<any>(`${environment.authApiUrl}/admin/users`, { params: httpParams, withCredentials: true }).pipe(catchError(this.handleError));
  }

  /** Get user stats (admin). */
  getUserStats(): Observable<any> {
    return this.http.get<any>(`${environment.authApiUrl}/admin/users/stats`, { withCredentials: true }).pipe(catchError(this.handleError));
  }

  /** Get admin endpoint activity logs (admin). */
  getAdminLogs(filter: string): Observable<any> {
    let httpParams = new HttpParams().set('filter', filter);
    return this.http.get<any>(`${environment.authApiUrl}/admin/logs`, { params: httpParams, withCredentials: true }).pipe(catchError(this.handleError));
  }

  /** Get detailed transaction and calculation audit logs (admin). */
  getAdminTransactionLogs(filter: string, type?: string): Observable<any> {
    let httpParams = new HttpParams().set('filter', filter);
    if (type) {
      httpParams = httpParams.set('type', type);
    }
    return this.http.get<any>(`${environment.authApiUrl}/admin/logs/transactions`, { params: httpParams, withCredentials: true }).pipe(catchError(this.handleError));
  }



  /** Update user status (admin). */
  updateUserStatus(userId: string, isActive: boolean): Observable<any> {
    return this.http.patch<any>(`${environment.authApiUrl}/admin/users/${userId}/status`, { isActive }, { withCredentials: true }).pipe(catchError(this.handleError));
  }

  /** Update vendor approval status (admin). */
  updateVendorApprovalStatus(userId: string, approvalStatus: string): Observable<any> {
    return this.http.patch<any>(`${environment.authApiUrl}/admin/users/${userId}/approve`, { approvalStatus }, { withCredentials: true }).pipe(catchError(this.handleError));
  }


  // ── Vendor: Self-service Profile ────────────────────────────

  /** Get vendor's own profile. */
  getVendorProfile(): Observable<any> {
    return this.http.get<any>(`${environment.authApiUrl}/vendor/profile`, { withCredentials: true }).pipe(catchError(this.handleError));
  }

  /** Get public vendor profile by ID. */
  getPublicVendorProfile(id: string): Observable<any> {
    return this.http.get<any>(`${environment.authApiUrl}/vendor/${id}`, { withCredentials: true }).pipe(catchError(this.handleError));
  }

  /** Update vendor profile. */
  updateVendorProfile(data: any): Observable<any> {
    return this.http.patch<any>(`${environment.authApiUrl}/vendor/profile`, data, { withCredentials: true }).pipe(catchError(this.handleError));
  }

  /** Get vendor stats. */
  getVendorStats(): Observable<any> {
    return this.http.get<any>(`${environment.authApiUrl}/vendor/stats`, { withCredentials: true }).pipe(catchError(this.handleError));
  }

  /** Get stored access token. */
  getAccessToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  /** Get stored refresh token. */
  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  }

  /** Store tokens and user profile after successful auth. */
  private handleAuthSuccess(response: AuthResponse): void {
    if (response.tokens) {
      localStorage.setItem(TOKEN_KEY, response.tokens.accessToken);
      localStorage.setItem(REFRESH_KEY, response.tokens.refreshToken);
    }
    localStorage.setItem(USER_KEY, JSON.stringify(response.user));
    this._user.set(response.user);
  }

  /** Clear stored auth data. */
  clearSession(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    this._user.set(null);
    this._isSynced = false;
  }

  /** Load user from localStorage at startup. */
  private loadStoredUser(): UserProfile | null {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  getCamRequirements(): Observable<any> {
    return this.http.get(`${this.apiUrl}/cams/me/insurance-requirements`, { withCredentials: true }).pipe(
      map(res => res || {}),
      catchError(() => of({}))
    );
  }

  updateCamRequirements(limits: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/cams/me/insurance-requirements`, limits, { withCredentials: true }).pipe(catchError(this.handleError));
  }

  getOrgRequirements(): Observable<any> {
    return this.http.get(`${this.apiUrl}/organizations/me/insurance-requirements`, { withCredentials: true }).pipe(
      map(res => res || {}),
      catchError(() => of({}))
    );
  }

  updateOrgRequirements(limits: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/organizations/me/insurance-requirements`, limits, { withCredentials: true }).pipe(catchError(this.handleError));
  }

  getVendorDocuments(): Observable<any> {
    return this.http.get(`${environment.authApiUrl}/vendor/documents`, { withCredentials: true });
  }

  uploadInsuranceDocument(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(`${environment.authApiUrl}/vendor/documents/upload-insurance`, formData, { withCredentials: true });
  }

  verifyInsuranceDocument(docId: string, payload: { expiryDate: string; insuranceValues: any }): Observable<any> {
    return this.http.post(`${environment.authApiUrl}/vendor/documents/${docId}/verify`, payload, { withCredentials: true });
  }

  /** Centralized error handling. */
  private handleError(error: any): Observable<never> {
    const message =
      error?.error?.message || error?.message || 'An unexpected error occurred.';
    return throwError(() => new Error(message));
  }
}
